import React, { useEffect } from "react";
import { JsonSchema, JsonSchemaProperty, JsonSchemaType } from "../../types";

export interface SchemaFormData {
    [key: string]: string;
}

interface RenderFormProps {
    jsonSchema: JsonSchema;
    formData: SchemaFormData;
    setFormData:
    (formData: SchemaFormData) => void;
    checkValidation: boolean;
    onValidated:
    () => void;
}

const RenderSchemaForm: React.FC<RenderFormProps> = ({
    jsonSchema,
    formData,
    setFormData,
    checkValidation,
    onValidated
}) => {


    const renderFormField = (name: string, schema: JsonSchemaProperty) => {
        const value = formData[name] ?? schema.default?.toString() ?? "";
        const isRequired = jsonSchema.required?.includes(name);

        if (schema.type === 'boolean') {
            return (
                <div className="mb-3 form-check" key={name}>
                    <input
                        type="checkbox"
                        className="form-check-input"
                        id={name}
                        checked={value === 'true'}
                        onChange={(e) => setFormData({ ...formData, [name]: e.target.checked.toString() })}
                        required={isRequired}
                    />
                    <label className="form-check-label" htmlFor={name}>
                        {name}
                        {isRequired && <span className="text-danger">*</span>}
                    </label>
                    {schema.description && (
                        <div className="form-text text-muted">
                            {schema.description}
                        </div>
                    )}
                </div>
            );
        }

        const getInputType = (type: JsonSchemaType) => {
            switch (type) {
                case 'integer':
                case 'number':
                    return 'number';
                default:
                    return 'text';
            }
        };

        const getStep = (type: JsonSchemaType) => {
            switch (type) {
                case 'integer':
                    return '1';
                case 'number':
                    return 'any';
                default:
                    return undefined;
            }
        };

        return (
            <div className="mb-3" key={name}>
                <label htmlFor={name} className="form-label">
                    {name}
                    {isRequired && <span className="text-danger">*</span>}
                </label>
                <input
                    type={getInputType(schema.type)}
                    className="form-control"
                    id={name}
                    value={value}
                    onChange={(e) => setFormData({ ...formData, [name]: e.target.value })}
                    min={schema.minimum}
                    max={schema.maximum}
                    step={getStep(schema.type)}
                    required={isRequired}
                    placeholder={schema.description || ""}
                />
                {schema.description && (
                    <div className="form-text text-muted">
                        {schema.description}
                    </div>
                )}
            </div>
        );
    };



    const validateFormData = (): boolean => {
        if (!jsonSchema) return true;

        const { properties, required = [] } = jsonSchema;

        for (const [key, prop] of Object.entries(properties)) {
            const value = formData[key];

            // Check required fields
            if (required.includes(key) && (value === undefined || value === '')) {
                alert(`${key} is required`);
                return false;
            }

            // Skip validation if field is empty and not required
            if (!value && !required.includes(key)) {
                continue;
            }

            // Type-specific validation
            switch (prop.type) {
                case 'number':
                case 'integer': {
                    const numValue = prop.type === 'integer' ? parseInt(value) : parseFloat(value);
                    if (isNaN(numValue)) {
                        alert(`${key} must be a valid ${prop.type}`);
                        return false;
                    }
                    if (prop.minimum !== undefined && numValue < prop.minimum) {
                        alert(`${key} must be greater than or equal to ${prop.minimum}`);
                        return false;
                    }
                    if (prop.maximum !== undefined && numValue > prop.maximum) {
                        alert(`${key} must be less than or equal to ${prop.maximum}`);
                        return false;
                    }
                    break;
                }
                case 'boolean': {
                    if (value !== 'true' && value !== 'false') {
                        alert(`${key} must be a boolean value`);
                        return false;
                    }
                    break;
                }
                // Add string validation if needed
                case 'string': {
                    // Could add string-specific validation here if needed
                    break;
                }
            }
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validateFormData()) {
            return;
        }

        // Convert form data to appropriate types for custom data
        const customData: { [key: string]: unknown } = {};
        // Add custom form data with proper type conversion
        if (jsonSchema?.properties) {
            Object.entries(jsonSchema.properties).forEach(([key, prop]) => {
                const value = formData[key];
                if (value !== undefined && value !== '') {
                    switch (prop.type) {
                        case 'integer':
                            customData[key] = parseInt(value);
                            break;
                        case 'number':
                            customData[key] = parseFloat(value);
                            break;
                        case 'boolean':
                            customData[key] = value === 'true';
                            break;
                        default:
                            customData[key] = value;
                    }
                } else if (prop.default !== undefined) {
                    customData[key] = prop.default;
                }
            });
        }

        onValidated()
    };

    useEffect(() => {
        // Initialize form data when workflow is selected
        if (jsonSchema.properties) {
            const initialData: SchemaFormData = {};
            Object.entries(jsonSchema.properties).forEach(([key, prop]) => {
                if (prop.default !== undefined) {
                    initialData[key] = prop.default.toString();
                } else if (prop.type === 'boolean') {
                    initialData[key] = 'false';
                } else if (prop.type === 'number' || prop.type === 'integer') {
                    initialData[key] = prop.minimum?.toString() ?? '0';
                } else {
                    initialData[key] = '';
                }
            });
            setFormData(initialData);
        }
    }, [jsonSchema, setFormData]);
    useEffect(() => {
        if (checkValidation) {
            handleSubmit()
        }
    }, [checkValidation]);

    return (
        <>
            {jsonSchema.properties &&
                Object.entries(jsonSchema.properties).map(([name, schema]) =>
                    renderFormField(name, schema)
                )
            }
        </>
    );
};

export default RenderSchemaForm;
